import React, { createContext, useContext, useEffect, useState } from 'react';

const THEME_KEY = 'app_theme';

export const palette = {
  brand: '#00D632',
  brandSoft: '#E1F9E7',
  ink: '#000000',
  surface: '#FFFFFF',
  line: '#F3F4F6',
  muted: '#9CA3AF',
} as const;

// Dark mode is the same six tokens with ink/surface swapped, plus dark-safe
// values for the two tints that cannot simply invert (borders and brand tint).
export const darkPalette = {
  brand: '#00D632',
  brandSoft: '#0B2E16',
  ink: '#FFFFFF',
  surface: '#000000',
  line: '#1F2937',
  muted: '#9CA3AF',
} as const;

type Palette = { [Key in keyof typeof palette]: string };

// Existing screens consume semantic aliases. Keep that public shape stable
// while every alias resolves to one of the six player-side design tokens.
const makeColors = (palette: Palette) => ({
  brand: palette.brand,
  brandSoft: palette.brandSoft,
  ink: palette.ink,
  line: palette.line,
  muted: palette.muted,
  bg: palette.surface,
  surface: palette.surface,
  surfaceRaised: palette.surface,
  surfaceMuted: palette.line,
  card: palette.surface,
  cardBorder: palette.line,
  border: palette.line,
  borderStrong: palette.muted,
  header: palette.surface,
  headerBorder: palette.line,
  text: palette.ink,
  textInverse: palette.surface,
  textSub: palette.ink,
  textMuted: palette.muted,
  input: palette.line,
  inputBorder: palette.line,
  inputText: palette.ink,
  sectionLabel: palette.ink,
  divider: palette.line,
  tabBar: palette.surface,
  tabBarBorder: palette.line,
  modalBg: palette.surface,
  skeletonBg: palette.line,
  overlay: palette.ink,
  shadow: palette.ink,
  accent: palette.brand,
  accentDark: palette.brand,
  accentPressed: palette.brand,
  accentSoft: palette.brandSoft,
  accentText: palette.ink,
  secondary: palette.ink,
  secondaryRaised: palette.surface,
  success: palette.brand,
  successSoft: palette.brandSoft,
  successText: palette.ink,
  warning: palette.ink,
  warningSoft: palette.line,
  warningText: palette.ink,
  danger: palette.ink,
  dangerSoft: palette.line,
  dangerText: palette.ink,
  info: palette.ink,
  infoSoft: palette.line,
  infoText: palette.ink,
});

export const lightColors = makeColors(palette);

export type ThemeColors = { [Key in keyof typeof lightColors]: string };

export const darkColors: ThemeColors = {
  ...makeColors(darkPalette),
  // Overlays and shadows are always cast in black, never in the theme's ink.
  overlay: '#000000',
  shadow: '#000000',
};

interface ThemeContextType {
  isDark: boolean;
  colors: ThemeColors;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDark: false,
  colors: lightColors,
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    try {
      setIsDark(localStorage.getItem(THEME_KEY) === 'dark');
    } catch {
      // Storage can be unavailable in private browsing.
    }
  }, []);

  useEffect(() => {
    const colors = isDark ? darkColors : lightColors;
    document.body.style.background = colors.bg;
    document.body.style.color = colors.text;
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.bg);
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(current => {
      const next = !current;
      try {
        localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
      } catch {
        // Storage can be unavailable in private browsing.
      }
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDark, colors: isDark ? darkColors : lightColors, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
